import { Button } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSearch } from 'react-icons/fi';
import { isEnvFeatureEnabled } from '@app/enviroment/enviroment';
import { useAuthContext } from '@context/auth/useAuthContext';
import { enabledEntities } from './entityRegistry';
import { GlobalSearchPalette } from './GlobalSearchPalette';

// Any visible antd modal: the shortcut must not stack the palette over another dialog.
const otherModalOpen = () => Boolean(document.querySelector('.ant-modal-wrap:not([style*="display: none"])'));

// userAgentData first: navigator.platform is deprecated and may go empty.
const isMacPlatform = () => {
  if (typeof navigator === 'undefined') return false;
  const uaPlatform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform;
  return /mac|iphone|ipad/i.test(uaPlatform || navigator.platform || navigator.userAgent);
};

export function GlobalSearchTrigger() {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [open, setOpen] = useState(false);
  const openRef = useRef(open);
  openRef.current = open;
  // isDoctor is only ever set for users without adm/ofa, so it needs no check here.
  const isOffice = Boolean(user.isAdmin || user.isOfficeAdmin);
  const isMac = isMacPlatform();
  const available = isOffice && isEnvFeatureEnabled('GLOBAL_SEARCH_ENABLED');
  const types = useMemo(
    () => enabledEntities({ isOffice, isAdmin: Boolean(user.isAdmin), flag: isEnvFeatureEnabled }).map((d) => d.type),
    [isOffice, user.isAdmin]
  );

  useEffect(() => {
    if (!available) return undefined;
    const onKey = (e: KeyboardEvent) => {
      // code covers non-Latin layouts (Greek gives key 'κ'); Ctrl+K stays native on macOS.
      const isK = e.code === 'KeyK' || e.key?.toLowerCase() === 'k';
      if (!(isMac ? e.metaKey : e.ctrlKey) || !isK || e.defaultPrevented) return;
      if (!openRef.current && otherModalOpen()) return;
      e.preventDefault();
      setOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [available, isMac]);

  if (!available) return null;
  return (
    <>
      <Button icon={<FiSearch />} onClick={() => setOpen(true)} className="!text-gray-500">
        <span className="hidden md:inline">{t('global-search-trigger')}</span>
        <kbd className="ml-2 hidden rounded border px-1 text-xs md:inline">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
      </Button>
      <GlobalSearchPalette open={open} onClose={() => setOpen(false)} types={types} userId={user.userId} />
    </>
  );
}
